<template>
  <v-menu open-on-click bottom offset-y>
    <!-- Activador del menú, que muestra la campana de notificaciones -->
    <template v-slot:activator="{ on, attrs }">
      <v-badge
        offset-x="30"
        offset-y="10"
        color="secondary"
        bottom
        :content="notificationCount > 0 ? notificationCount : '0'"
      >
        <v-btn
          dark
          text
          v-bind="attrs"
          v-on="on"
          style="border: ridge 1px transparent"
        >
          <v-img :src="notificationIcon" height="32" width="32"></v-img>
        </v-btn>
      </v-badge>
    </template>

    <!-- Contenedor para la lista de notificaciones con scroll -->
    <div class="notification--container">
      <!-- Lista de notificaciones si hay alguna -->
      <v-list v-if="hasNotifications" class="py-0">
        <v-list-item
          v-for="(item, index) in filteredNotifications"
          :key="index"
          class="px-0"
          @click="redirectTo(item)"
        >
          <!-- Lista de Notificaciones -->
          <v-list-item-title
            class="notification--item"
            :style="{
              'background-color': item.background + ' !important'
            }"
          >
            <v-list-item-subtitle class="mb-1">
              <!-- Ícono de la notificación dependiento del tipo -->
              <v-icon class="mr-1" size="15" style="color: #345d48">
                {{ getNotificationIcon(item.type) }}
              </v-icon>
              <!-- Mensaje de la notificación -->
              <span
                class="notification--text font-weight-bold"
                :style="{ color: getTextColor(item.background) }"
              >
                <template v-if="item.type === 5">
                  {{
                    $tc(getNotificationTypeName(item.type), 1)
                      .charAt(0)
                      .toUpperCase() +
                    $tc(getNotificationTypeName(item.type), 1).slice(1)
                  }}:
                </template>
                <template v-else>
                  {{ $tc(getNotificationTypeName(item.type), 1).toUpperCase() }}
                </template>
              </span>
            </v-list-item-subtitle>

            <template
              v-if="item.type === 5 && getNotificationDetails(item.message)"
            >
              <!-- Nueva Vista Suborden -->
              <v-list-item-subtitle class="mt-1">
                <div
                  class="notification--text"
                  :style="{ color: getTextColor(item.background) }"
                >
                  <div>
                    <strong>{{ $t('code') }}:</strong>
                    {{ getNotificationDetails(item.message).code }}
                  </div>
                  <div class="text-truncate">
                    <strong>{{ $t('name') }}:</strong>
                    {{ getNotificationDetails(item.message).name }}
                  </div>
                  <div>
                    <strong>{{ $t('current_status') }}:</strong>
                    {{ getNotificationDetails(item.message).status }}
                  </div>
                  <div>
                    <strong>{{ $t('promise_date') }}:</strong>
                    {{
                      dateFormat(
                        getNotificationDetails(item.message).delivery_date
                      )
                    }}
                  </div>
                </div>
              </v-list-item-subtitle>
            </template>

            <template v-else>
              <!-- Nombre de la Sub-orden / Mensaje general -->
              <v-list-item-subtitle>
                <span
                  class="notification--text"
                  :style="{ color: getTextColor(item.background) }"
                >
                  {{ getTranslatedMessage(item.message) }}
                </span>
              </v-list-item-subtitle>

              <!-- Fecha de Entrega -->
              <v-list-item-subtitle
                v-if="getNotificationDetails(item.message)"
                class="mt-1"
              >
                <span
                  class="notification--text"
                  style="font-size: 11px"
                  :style="{ color: getTextColor(item.background) }"
                >
                  {{ $t('promise_date') }}:
                  {{
                    dateFormat(
                      getNotificationDetails(item.message).delivery_date
                    )
                  }}
                </span>
              </v-list-item-subtitle>
              <!-- Fecha de la Notificación (Gastos, Chat, Mantenimiento) -->
              <v-list-item-subtitle v-else-if="item.timestamp" class="mt-1">
                <span
                  class="notification--text"
                  style="font-size: 11px"
                  :style="{ color: getTextColor(item.background) }"
                >
                  {{ formatTimestamp(item.timestamp) }}
                </span>
              </v-list-item-subtitle>
            </template>
          </v-list-item-title>
        </v-list-item>
      </v-list>
      <v-list v-else>
        <v-list-item>
          <v-list-item-title>
            {{ $t('no_notifications') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </div>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex'
import { isTokenExpired } from '@/store/session'

// Tipo de notificación de Whatsapp, según el fixture del módulo
const NOTIFICATION_TYPE_WHATSAPP = 7

export default {
  props: {
    mode: {
      type: String
    }
  },
  data() {
    return {
      notifications: [],
      current: 0,
      notifications_types: [
        { id: 1, name: 'maintenance' },
        { id: 2, name: 'expense' },
        { id: 3, name: 'chat' },
        { id: 4, name: 'order' },
        { id: 5, name: 'suborder' },
        { id: 6, name: 'inventory' },
        { id: 7, name: 'whatsapp' }
      ],
      socket: null
    }
  },
  computed: {
    ...mapGetters({
      hasWhatsappAddon: 'whatsapp/getHasAddon',
      getTotalSuborderNotifications:
        'notifications/getTotalSuborderNotifications',
      getTotalGeneralNotifications:
        'notifications/getTotalGeneralNotifications',
      isAdmin: 'session/isAdmin',
      me: 'session/me',
      token: 'session/token',
      company: 'company/getCompanyData'
    }),
    /**
     * Filtra las notificaciones según el modo.
     */
    filteredNotifications() {
      const suborderType = 5
      // WhatsApp se vende como complemento. Sin contratarlo no llegan nuevas,
      // pero pueden quedar viejas de cuando lo tenía: tampoco se muestran.
      const visibles = this.hasWhatsappAddon
        ? this.notifications
        : this.notifications.filter(
            (notification) => notification.type !== NOTIFICATION_TYPE_WHATSAPP
          )

      if (this.mode === 'events') {
        return visibles.filter(
          (notification) => notification.type === suborderType
        )
      } else {
        return visibles.filter(
          (notification) => notification.type !== suborderType
        )
      }
    },
    /**
     * Verifica si hay notificaciones disponibles.
     *
     * @returns {boolean}
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    hasNotifications() {
      return this.notificationCount > 0
    },
    /**
     * Cuenta el número de notificaciones.
     *
     * @returns {number} Número de notificaciones a mostrar.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     * @author Ing. Argenis Osorio <argenisosorio10@gmail.com>
     */
    notificationCount() {
      return this.filteredNotifications.length
    },
    /**
     * Selecciona el ícono de notificación basado en el modo actual.
     *
     * @returns {string} Ruta del ícono de notificación.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    notificationIcon() {
      return this.mode === 'notifications'
        ? require('@/assets/color-icons/settings/notifications.png')
        : require('@/assets/color-icons/toolbar/notification.png')
    }
  },
  methods: {
    /**
     * Muestra el ícono de cada notificación dependiendo del tipo.
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    getNotificationIcon(type) {
      const iconMap = {
        1: 'fas fa-wrench', // Mantenimiento
        2: 'fas fa-money-bill-alt', // Gasto
        3: 'fas fa-comments', // Chat
        4: 'fas fa-tag', // Orden
        5: 'fas fa-tag', // Suborden
        6: 'fas fa-box' // Inventario
      }
      return iconMap[type] || 'fas fa-tag' // Icono por defecto
    },

    getTextColor(background) {
      if (!background) return 'var(--v-muted-darken3)'
      const cleanBg = background.toLowerCase().trim()
      if (
        cleanBg === '#28a745' ||
        cleanBg === '#4caf50' ||
        cleanBg === '#ff5722'
      ) {
        return '#000000'
      }
      // Si el color de fondo es oscuro, usar texto blanco, de lo contrario texto negro/oscuro
      return this.calculateColor(background) ? '#ffffff' : '#000000'
    },

    /**
     * Obtener y mostrar el tipo de cada notificación listada.
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    getNotificationTypeName(id) {
      const type = this.notifications_types.find((element) => element.id === id)
      return type ? type.name : ''
    },
    /**
     * Obtener todas las notificaciones no leídas desde la API.
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    loadNotificationsAPI() {
      const companyId = this.company?.pk
      const params = companyId ? `?company=${companyId}` : ''
      axios
        .get(`notifications/notifications/unread/${params}`)
        .then((response) => {
          this.notifications = response.data
        })
        .catch((error) => {
          console.log('error')
        })
    },

    /**
     * isValidJSON
     *
     * Verifica si una cadena es un JSON válido.
     *
     * @param {string} str - Cadena a validar.
     *
     * @returns {boolean} Verdadero si la cadena es un JSON válido, falso en
     * caso contrario. Ejemplo de dato guardado:
     * {"order":"1","suborden":"1","message":"Notificación de Suborden"}
     *
     * Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    isValidJSON(str) {
      try {
        JSON.parse(str)
        return true
      } catch {
        return false
      }
    },

    /**
     * getTranslatedMessage
     *
     * Traduce el mensaje de notificación si es un JSON.
     *
     * @param {string} jsonStr - Cadena JSON a procesar.
     *
     * @returns {string} El mensaje traducido o el texto original.
     * Soporta dos formatos:
     * 1. Nuevo (Inventario/Suborden): {key: "...", kwargs: {...}} -> Traducido con $t
     * 2. Antiguo: {message: "..."} -> Retorna message
     */
    getTranslatedMessage(jsonStr) {
      try {
        const data = JSON.parse(jsonStr)
        // Verificar nueva estructura {key: "...", kwargs: {...}}
        if (data.key && data.kwargs) {
          // Si hay un parámetro 'status', intentar traducirlo recursivamente
          if (data.kwargs.status) {
            data.kwargs.status = this.$t(data.kwargs.status)
          }
          return this.$t(data.key, data.kwargs)
        }
        // Fallback para estructura antigua {message: "..."}
        if (data.message) {
          return data.message
        }
        return jsonStr
      } catch (e) {
        return jsonStr
      }
    },
    getNotificationDetails(jsonStr) {
      try {
        const data = JSON.parse(jsonStr)
        if (data.kwargs && data.kwargs.delivery_date) {
          return {
            date_created: data.kwargs.date_created || '',
            delivery_date: data.kwargs.delivery_date,
            code: data.kwargs.code || '',
            // status is translated here to display properly in the UI
            status: data.kwargs.status ? this.$t(data.kwargs.status) : '',
            name: data.kwargs.name || ''
          }
        }
        return null
      } catch (e) {
        return null
      }
    },
    /**
     * Formatea un timestamp a formato de fecha y hora.
     *
     * @param {string} timestamp - Timestamp a formatear.
     *
     * @returns {string} Timestamp formateado.
     */
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      const mmnt = this.$moment.utc(timestamp)
      const dateStr = mmnt.format('YYYY-MM-DD')
      const timeStr = mmnt.format('HH:mm')
      return `${this.dateFormat(dateStr)} ${timeStr}`
    },
    /**
     * Establece la conexión WebSocket para recibir notificaciones en tiempo real.
     *
     * Lógica implementada:
     * 1. Verifica si ya existe una conexión activa.
     * 2. Obtiene el token de sesión (JWT) para autenticación.
     * 3. Determina la URL del WebSocket (env var o construcción dinámica).
     * 4. Conecta al servidor pasando el token como parámetro (?token=...).
     * 5. Maneja mensajes entrantes, errores y reconexión automática.
     */
    async redirectTo(item) {
      if (!item.type) return

      // Marcar como leída solo si NO es gasto (2), mantenimiento (1), inventario
      const note_for_not_read = [1, 2, 6]
      if (!note_for_not_read.includes(item.type)) {
        axios
          .post(`notifications/notifications/${item.pk}/mark-as-read/`)
          .then(() => {
            // Eliminar de la lista local
            this.notifications = this.notifications.filter(
              (n) => n.pk !== item.pk
            )
          })
          .catch((e) => {
            console.error('Error marking as read', e)
          })
      }

      // Redireccionar según el tipo
      if (item.type === NOTIFICATION_TYPE_WHATSAPP) {
        // Whatsapp: abrir la bandeja en la conversación correspondiente
        try {
          this.$router.push({
            name: 'whatsapp',
            query: { conversation: item.stored_id }
          })
        } catch (e) {
          console.error('Error redirection to whatsapp inbox', e)
        }
      } else if (item.type === 5 || item.type === 3) {
        // Suborden/Orden (5) o Chat (3)
        // Tab 4 corresponde a "Notas/Chat" en DialogProduct.vue
        const tab = item.type === 3 ? 4 : 1
        const payload = { id: item.stored_id, tab: tab }

        try {
          if (this.$route.name === 'sale') {
            // Si ya estamos en la vista de ventas, emitir evento global (EventBus)
            // para evitar recargas innecesarias del router y mantener el estado.
            this.$root.$emit('open-modal-suborder', payload)
          } else {
            // Utilizar el stored_id que corresponde al ID de la suborden (OrderProduct)
            this.$router.push({
              name: 'sale',
              query: { openModal: item.stored_id, tab: tab }
            })
          }
        } catch (e) {
          console.error('Error redirection to sale list with modal', e)
        }
      } else if (item.type === 2) {
        // Gastos (2)
        try {
          const dateStr = this.$moment.utc(item.timestamp).format('YYYY-MM-DD')
          this.$router.push({
            name: 'expensesRegular-pk',
            params: { pk: this.codification({ code: 'create' }) },
            query: { base_id: item.stored_id, event_id: item.pk, date: dateStr }
          })
        } catch (e) {
          console.error('Error redirection to expense create form', e)
        }
      } else if (item.type === 1) {
        // Mantenimiento (1) -> Redirigir al modal de pagos del mantenimiento
        try {
          const res = await this.$api.maintenance.show({ pk: item.stored_id })
          const maint = res.data
          const equipment = maint.equipement || {
            pk: maint.equipement_id,
            name: ''
          }

          if (this.$route.name === 'maintenance') {
            this.$root.$emit('open-modal-maintenance-payment', {
              maintenanceId: maint.pk,
              eventId: item.pk,
              equipment: equipment
            })
          } else {
            this.$router.push({
              name: 'maintenance',
              params: { equipment: JSON.stringify(equipment) },
              query: { openPayment: maint.pk, event_id: item.pk }
            })
          }
        } catch (e) {
          console.error('Error redirection to maintenance payment modal', e)
        }
      } else if (item.type === 6) {
        this.$router.push({
          name: 'purchase-pk',
          params: { pk: this.codification({ code: 'create' }) }
        })
      }
    },
    connectWebSocket() {
      // Evitar múltiples conexiones si está abierto O intentando conectar (Ghost Sockets)
      if (
        this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING)
      ) {
        return
      }

      // El token no se renueva: si ya venció, reintentar sólo gasta y
      // esconde el fallo detrás de un error de consola cada treinta segundos.
      const token = this.token
      if (!token || isTokenExpired(token)) return

      let wsUrl = ''

      if (process.env.NODE_ENV === 'production') {
        const type = process.env.VUE_APP_SERVER_WEBSOCKET_TYPE || 'wss://'
        const host = window.location.hostname
        const port = process.env.VUE_APP_SERVER_WEBSOCKET_PORT || ''
        wsUrl = `${type}${host}${port}/ws/notifications/`
      } else {
        wsUrl = process.env.VUE_APP_WEBSOCKET || ''
        if (wsUrl.includes('/chat/')) {
          wsUrl = wsUrl.replace('/chat/', '/ws/notifications/')
        } else if (!wsUrl) {
          const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
          const host = window.location.hostname
          const port = host === 'localhost' ? ':8000' : ''
          wsUrl = `${protocol}://${host}${port}/ws/notifications/`
        }
      }

      this.socket = new WebSocket(`${wsUrl}?token=${token}`)

      this.socket.onmessage = (e) => {
        const data = JSON.parse(e.data)

        // Evento de eliminación en tiempo real emitido por el backend
        if (data.action === 'delete') {
          this.notifications = this.notifications.filter(
            (n) => n.pk !== data.pk
          )
          return
        }

        // Constante descriptiva para evitar 'Números Mágicos'
        const NOTIFICATION_TYPE_SUBORDER = 5

        // Filtrar notificaciones de suborden y whatsapp de otra empresa
        if (
          data.type === NOTIFICATION_TYPE_SUBORDER ||
          data.type === NOTIFICATION_TYPE_WHATSAPP
        ) {
          try {
            const messageObj = JSON.parse(data.message)
            if (messageObj.company && messageObj.company !== this.company?.pk) {
              return
            }
          } catch (err) {
            // Se silencia permitiendo texto plano
          }

          // Eliminar la notificación anterior no leída del mismo origen
          const existingIndex = this.notifications.findIndex(
            (n) => n.type === data.type && n.stored_id === data.stored_id
          )
          if (existingIndex !== -1) {
            this.notifications.splice(existingIndex, 1)
          }
        }
        // El contador de la barra superior escucha esto para volver a
        // contar sin mantener un sondeo propio.
        if (data.type === NOTIFICATION_TYPE_WHATSAPP) {
          this.$root.$emit('whatsapp-notification')
        }

        this.notifications.unshift(data)
      }

      this.socket.onclose = (e) => {
        if (!e.wasClean) {
          // Incrementador de reintentos dinámico (Exponential Backoff simple)
          this.reconnectAttempts = (this.reconnectAttempts || 0) + 1
          const delay = Math.min(this.reconnectAttempts * 2000, 30000)

          console.warn(
            `WebSocket cerrado de forma inesperada. Reintentando en ${
              delay / 1000
            }s...`
          )

          this.reconnectTimeout = setTimeout(() => {
            this.connectWebSocket()
          }, delay)
        }
      }

      this.socket.onerror = (err) => {
        console.error('WebSocket encountered an error: ', err)
        this.socket.close()
      }
    }
  },
  mounted() {
    this.loadNotificationsAPI()
    this.connectWebSocket()
    this.$root.$on('payment-processed', this.loadNotificationsAPI)
  },
  beforeDestroy() {
    this.$root.$off('payment-processed', this.loadNotificationsAPI)
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }
    if (this.socket) {
      this.socket.onclose = null
      this.socket.close()
    }
  }
}
</script>

<style>
.v-list-item--dense,
.v-list--dense .v-list-item {
  min-height: 20px;
}

.suborder-style {
  width: 20%;
  overflow: hidden;
  box-sizing: border-box;
  border: ridge 1px transparent;
  margin-left: -25px;
}

.notification--container {
  max-height: 500px;
  overflow-y: auto;
  width: 400px;
}

.notification--item {
  padding: 1rem;
  border-bottom: 1px solid rgba(132, 132, 132, 0.29);
}

.notification--text {
  font-size: 13px;
  color: inherit;
}
</style>
